import dayjs from 'dayjs';
import * as yup from 'yup';

import {
  Field,
  Fields,
  FieldTypes,
  LetterTypes,
  Option,
  Sources,
} from './types';

export const dateSchema = yup
  .string()
  .trim()
  .test('is-date', 'Invalid date format', value => dayjs(value).isValid());

export const heightSchema = yup
  .string()
  .trim()
  .matches(/^[0-9]+'([0-9]+)"$/, 'Height must be in the format X\'Y"');

export const weightSchema = yup.number().positive();

export const medicalFacilityOrProviderField: Field = {
  name: Fields.medicalFacilityOrProvider,
  label: 'Medical Facility / Provider',
  type: FieldTypes.text,
  schema: yup.string().trim().required(),
};

export const ratableHistoryField: Field = {
  name: Fields.ratableHistory,
  label: 'Ratable History',
  type: FieldTypes.text,
  schema: yup.string().trim().required(),
};

export const declinableHistoryField: Field = {
  name: Fields.declinableHistory,
  label: 'Declinable History',
  type: FieldTypes.text,
  schema: yup.string().trim().required(),
};

export const heightField: Field = {
  name: Fields.height,
  label: 'Height',
  type: FieldTypes.text,
  schema: heightSchema.required(),
};

export const weightField: Field = {
  name: Fields.weight,
  label: 'Weight (lbs)',
  type: FieldTypes.text,
  schema: weightSchema.required(),
};

export const dateMeasurementTakenField: Field = {
  name: Fields.dateMeasurementTaken,
  label: 'Date Measurement Taken',
  type: FieldTypes.date,
  schema: dateSchema.required(),
};

export const applicableQuestionnaireField: Field = {
  name: Fields.applicableQuestionnaire,
  label: 'Applicable Questionnaire',
  type: FieldTypes.text,
  schema: yup.string().trim().required(),
};

export const customContentField: Field = {
  name: Fields.customContent,
  label: 'Text',
  type: FieldTypes.textarea,
  schema: yup.string().trim().max(2000).required(),
};

export const riskGroupField: Field = {
  name: Fields.riskGroup,
  label: 'Risk Group',
  type: FieldTypes.text,
  schema: yup.string().trim().required(),
};

export const riskDetailField: Field = {
  name: Fields.riskDetail,
  label: 'Risk Detail',
  type: FieldTypes.text,
  schema: yup.string().trim().required(),
};

export const referenceField: Field = {
  name: Fields.reference,
  label: 'Reference',
  type: FieldTypes.text,
  schema: yup.string().trim().required(),
};

export const diagnosisField: Field = {
  name: Fields.diagnosis,
  label: 'Diagnosis',
  type: FieldTypes.text,
  schema: yup.string().trim().required(),
};

export const riskField: Field = {
  name: Fields.risk,
  label: 'Risk',
  type: FieldTypes.text,
  schema: yup.string().trim().required(),
};

export const informationField: Field = {
  name: Fields.information,
  label: 'Information',
  type: FieldTypes.text,
  schema: yup.string().trim().required(),
};

export const labDrawDateField: Field = {
  name: Fields.dateMeasurementTaken,
  label: 'Lab Draw Date',
  type: FieldTypes.date,
  schema: dateSchema.required(),
};

export const labCarrierNameField: Field = {
  name: Fields.information,
  label: 'Carrier Name (if known on lab slip)',
  type: FieldTypes.text,
  schema: yup.string().trim().optional(),
  placeholder: '(optional)',
};

export const piiDataTypeField: Field = {
  name: Fields.piiDataType,
  label: 'PII Data Type',
  type: FieldTypes.text,
  schema: yup.string().trim().required(),
};

export const sourceOptions: Option<Sources>[] = [
  { label: 'App info / Disclosure', value: Sources.appInfoDisclosure },
  { label: 'APS', value: Sources.aps },
  { label: 'Build disclosed on app', value: Sources.buildDisclosedOnApp },
  { label: 'Build in APS', value: Sources.buildInAps },
  { label: 'Criminal History', value: Sources.criminalHistory },
  { label: 'ETI', value: Sources.eti },
  { label: 'Financial', value: Sources.financial },
  { label: 'LabPiQture', value: Sources.labPiQture },
  { label: 'Rx + ETI', value: Sources.rxEti },
  { label: 'MIB + ETI', value: Sources.mibAppEti },
  { label: 'MIB + APS', value: Sources.mibAps },
  {
    label: 'MIB + Rx / Med Claims',
    value: Sources.mibRxOrMedClaimsMilliman,
  },
  { label: 'Med Claims + APS', value: Sources.medClaimsAps },
  { label: 'MIB + Insurance Labs', value: Sources.mibInsuranceLabs },
  { label: 'MIB + LabPiQture', value: Sources.mibLabPiQture },
  { label: 'MVR', value: Sources.mvr },
  {
    label: 'Rx or Med Claims (Milliman)',
    value: Sources.rxOrMedClaimsMilliman,
  },
  { label: 'Rx or Med Claims (ExamOne)', value: Sources.rxOrMedClaimsExamOne },
  { label: 'TRL', value: Sources.trl },
  { label: 'Medical condition', value: Sources.medicalCondition },
  { label: 'Lack of APS', value: Sources.lackOfAps },
  { label: 'Lack of Information', value: Sources.lackOfInformation },
  { label: 'Miscellaneous', value: Sources.miscellaneous },
  { label: 'PII', value: Sources.pii },
  { label: 'Special character', value: Sources.specialCharacter },
  { label: 'Unresolved MIB', value: Sources.unresolvedMib },
  { label: 'Custom', value: Sources.custom },
];

export const sourceHiddenLetterTypes = [
  LetterTypes.substandard,
  LetterTypes.reinstatementApproval,
  LetterTypes.reinstatementWithdrawal,
];

export const LETTER_DEFAULT_PHONE = '415-915-0665';
export const LETTER_DEFAULT_EMAIL = 'accounts@ethoslife.com';
